import { CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const ANCHOR_GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 16;

export interface AnchoredPosition {
  top: number;
  left: number;
}

interface Viewport {
  width: number;
  height: number;
}

interface UseAnchoredPositionOptions {
  anchorEl?: HTMLElement | null;
  enabled: boolean;
}

const clamp = (value: number, max: number) => Math.min(Math.max(value, VIEWPORT_MARGIN_PX), Math.max(max, VIEWPORT_MARGIN_PX));

/**
 * Below the anchor with right edges aligned, flipped above when there isn't
 * room, then pulled inside the viewport. Pure, so it needs no DOM to test.
 */
export const positionFor = (anchor: DOMRect, target: DOMRect, viewport: Viewport): AnchoredPosition => {
  const maxTop = viewport.height - target.height - VIEWPORT_MARGIN_PX;
  const below = anchor.bottom + ANCHOR_GAP_PX;
  const top = below > maxTop ? anchor.top - target.height - ANCHOR_GAP_PX : below;

  return {
    top: clamp(top, maxTop),
    left: clamp(anchor.right - target.width, viewport.width - target.width - VIEWPORT_MARGIN_PX),
  };
};

/**
 * Positions an element against an anchor element, tracking window resizes.
 * Returns no style when there is no usable anchor, so the caller keeps its own
 * default placement.
 */
export const useAnchoredPosition = ({ anchorEl, enabled }: UseAnchoredPositionOptions) => {
  const frameRef = useRef(0);
  // State, not a ref: attaching has to re-run the effects below, and the target
  // mounts on a later commit than the open flip.
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [position, setPosition] = useState<AnchoredPosition | null>(null);

  const measure = useCallback(() => {
    // A row can unmount under a list refresh while the dialog is still open.
    if (typeof window === 'undefined' || !enabled || !target || !anchorEl?.isConnected) return;

    const anchorRect = anchorEl.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    if (!targetRect.width || !targetRect.height) return;

    const next = positionFor(anchorRect, targetRect, { width: window.innerWidth, height: window.innerHeight });
    setPosition((prev) => (prev && prev.top === next.top && prev.left === next.left ? prev : next));
  }, [anchorEl, enabled, target]);

  // A callback ref, not an object one: portalled dialogs (rc-dialog gates its
  // content on internal state) mount on a later commit than the open flip, so a
  // layout effect alone would measure a node that doesn't exist yet. This fires
  // when the node actually attaches, still before paint.
  const measureRef = useCallback((node: HTMLElement | null) => {
    setTarget(node);
    // Released only once the target unmounts -- clearing on close instead would
    // drop the offsets mid-animation and snap the dialog to centre.
    if (!node) setPosition(null);
  }, []);

  useLayoutEffect(() => {
    if (enabled) measure();
  }, [enabled, measure]);

  // A portalled dialog is display:none until its open animation starts, so the
  // first measurement reads 0x0. This re-measures the moment it gains size,
  // and keeps the placement honest if the content reflows.
  useEffect(() => {
    if (!enabled || !target || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => measure());
    observer.observe(target);
    return () => observer.disconnect();
  }, [enabled, measure, target]);

  useEffect(() => {
    if (!enabled || !anchorEl) return;

    // One frame per burst keeps a drag-resize from thrashing layout.
    const handleResize = () => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(measure);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [anchorEl, enabled, measure]);

  const style: CSSProperties | undefined = position ? { ...position, margin: 0 } : undefined;

  return { measureRef, style };
};
